import * as React from 'react';

import type {
  CloneSubmissionResultDto,
  RateCollectionApiError,
} from '@/api/rate-collection-client';
import { useCloneSubmission, usePriorCampaigns } from '@/api/use-rate-collection';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { submissionStateLabel } from '@/lib/rate-collection-labels';
import type { PriorCampaignDto } from '@/api/rate-collection-client';

// S35-CLONE-SKILLS-UX #5 (DBU §3.5 l.2840) : libellé d'option « intitulé — statut · date » du sélecteur
// de clonage. La date (ISO) est rendue en date locale FR courte ; absente ⇒ omise (jamais « null »).
function cloneOptionLabel(c: PriorCampaignDto): string {
  const statut = submissionStateLabel(c.submissionState).label;
  if (c.date === null) {
    return `${c.intitule} — ${statut}`;
  }
  const d = new Date(c.date);
  const dateFr = Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return dateFr === null
    ? `${c.intitule} — ${statut}`
    : `${c.intitule} — ${statut} · ${dateFr}`;
}

// =============================================================================
// clone-panel.tsx — UI de CLONAGE par copie inter-campagne (S13, D2 / G0-S13-4).
// =============================================================================
// Le fournisseur sélectionne une campagne ANTÉRIEURE qu'il a participée ; POST clone (D1-A)
// peuple le Brouillon courant par copie INDÉPENDANTE. Les warnings (profils non communs /
// compétences hors référentiel courant) sont AFFICHÉS mais NON bloquants (G0-S13-4). La grille
// est ensuite rafraîchie (invalidation de la query grille, câblée dans useCloneSubmission).
//
// D4 SEAM : la liste des campagnes antérieures est CÂBLÉE au GET prior-campaigns (usePriorCampaigns) —
// plus aucune prop de données. L'API résout la liste sous le tenant de session (autorité). Le panneau
// n'apparaît que s'il existe ≥ 1 campagne antérieure participée (sinon rien à cloner).
//
// a11y WCAG 2.1 AA : <select> labellisé, retour de clonage en role=status (succès) / role=alert
// (erreur 409 clone-no-prior-participation), warnings en liste sémantique. Aucune couleur seule.

export interface ClonePanelProps {
  readonly submissionId: string;
  /** Désactive le clonage (grille verrouillée / deadline dépassée). */
  readonly disabled: boolean;
}

export function ClonePanel({
  submissionId,
  disabled,
}: ClonePanelProps): React.JSX.Element | null {
  const priorCampaignsQuery = usePriorCampaigns(submissionId);
  const cloneMutation = useCloneSubmission(submissionId);
  const [selected, setSelected] = React.useState<string>('');
  // S35 #5 (DBU l.2842) : la copie ÉCRASE de façon IRRÉVERSIBLE les saisies communes ⇒ confirmation
  // explicite AVANT tout POST clone (aucun écrasement muet).
  const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);

  const priorCampaigns = priorCampaignsQuery.data?.campaigns ?? [];

  // Pas de campagne antérieure (ou chargement/échec) : rien à cloner, on n'affiche pas le panneau.
  if (priorCampaigns.length === 0) {
    return null;
  }

  const result: CloneSubmissionResultDto | undefined = cloneMutation.data;
  const error = cloneMutation.error as RateCollectionApiError | null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Réutiliser une campagne antérieure</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          Copiez vos taux d’une campagne précédente vers ce brouillon. Les profils absents de
          la grille actuelle sont ignorés (signalés ci-dessous).
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="clone-source" className="text-sm font-medium text-foreground">
              Campagne à copier
            </label>
            <select
              id="clone-source"
              value={selected}
              disabled={disabled || cloneMutation.isPending}
              onChange={(e) => setSelected(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Sélectionnez une campagne…</option>
              {priorCampaigns.map((c) => (
                // #5 (DBU l.2840) : intitulé + STATUT (+ date si l'API la fournit).
                <option key={c.campaignId} value={c.campaignId}>
                  {cloneOptionLabel(c)}
                </option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={disabled || selected === '' || cloneMutation.isPending}
            // #5 (DBU l.2842) : n'appelle PAS le clone directement — ouvre l'avertissement d'irréversibilité.
            onClick={() => setConfirmOpen(true)}
          >
            {cloneMutation.isPending ? 'Copie en cours…' : 'Copier les taux'}
          </Button>
        </div>

        {/* #5 (DBU l.2842) : avertissement préalable EXPLICITE du caractère IRRÉVERSIBLE de l'écrasement. */}
        <ConfirmDialog
          open={confirmOpen}
          title="Écrasement irréversible des saisies communes"
          description="Copier cette campagne écrase définitivement vos saisies en cours sur les profils communs avec la source. Cette action est irréversible. Les profils non communs sont préservés."
          confirmLabel="Écraser et copier"
          cancelLabel="Annuler"
          confirmDisabled={cloneMutation.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            cloneMutation.mutate({ sourceCampaignId: selected });
          }}
        />

        {error !== null && (
          <p role="alert" className="text-sm text-destructive">
            {error.kind === 'clone-no-prior-participation'
              ? 'Vous n’avez pas participé à cette campagne : aucune donnée à copier.'
              : error.message}
          </p>
        )}

        {result !== undefined && (
          <div role="status" className="flex flex-col gap-2 text-sm">
            <p className="text-foreground">
              {result.cellsCloned} taux copié{result.cellsCloned > 1 ? 's' : ''}
              {result.skillsCloned > 0
                ? ` et ${result.skillsCloned} compétence${result.skillsCloned > 1 ? 's' : ''}`
                : ''}
              . Votre grille a été mise à jour.
            </p>
            {result.warnings.length > 0 && (
              <div className="rounded-md border border-border bg-muted p-3">
                <p className="font-medium text-foreground">
                  Éléments non copiés ({result.warnings.length})&nbsp;:
                </p>
                <ul className="mt-1 list-inside list-disc text-muted-foreground">
                  {result.warnings.map((w) => (
                    <li key={`${w.code}-${w.profileCode}-${w.skillCode ?? ''}`}>{w.message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
